export type Role = 'super_admin' | 'admin' | 'co_traveller'

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  co_traveller: 'Co-traveller',
}

/** Both admin roles manage people, documents and invites. */
export const isAdmin = (r?: Role) => r === 'admin' || r === 'super_admin'
/**
 * Not a union any more: the party can add their own, so a category id is
 * whatever is in `expense_categories` plus the six built-ins below.
 */
export type Category = string
export type BuiltinCategory = 'food' | 'transport' | 'stay' | 'tickets' | 'shopping' | 'other'
export type SplitMode = 'equal' | 'exact' | 'none'
export type BookingKind = 'flight' | 'train' | 'bus' | 'cab' | 'stay' | 'other'
export type ItemKind =
  | 'sight' | 'meal' | 'transfer' | 'rest' | 'stay'
  | 'shopping' | 'event' | 'admin' | 'other'
export type TravelMode =
  | 'walk' | 'auto' | 'car' | 'taxi' | 'train' | 'flight' | 'boat' | 'camel' | 'other'
export type TicketStatus = 'not_needed' | 'todo' | 'booked'
export type ActivityStatus = 'planned' | 'done' | 'skipped'
export type DocumentKind =
  | 'passport' | 'visa' | 'citizenship' | 'id' | 'ticket' | 'hotel'
  | 'insurance' | 'permit' | 'licence' | 'other'

export interface Synced {
  id: string
  updated_at: number
  deleted_at: number | null
  created_by: string
}

export interface User {
  id: string
  email: string
  display_name: string
  avatar_color: string
  role: Role
  status: 'active' | 'disabled'
  updated_at: number
}

export interface Trip extends Synced {
  name: string
  start_date: string
  end_date: string
  base_currency: string
  archived: number
  country: string | null
  /** IANA zone, so "9:30 am" means 9:30 where you are standing. */
  timezone: string
  summary: string | null
}

export interface Place extends Synced {
  trip_id: string
  name: string
  arrive_date: string
  depart_date: string
  lat: number | null
  lng: number | null
  sort_order: number
}

/** One thing you do, at a time, in a place. Destination-agnostic by design. */
export interface Item extends Synced {
  trip_id: string
  place_id: string | null
  starts_at: number | null
  ends_at: number | null
  duration_min: number | null
  day_date: string | null
  title: string
  kind: ItemKind
  short_description: string | null
  /** Rich text as markdown. */
  details_md: string | null
  notes: string | null
  tips: string | null

  /** Estimate. Actual money lives in expenses. */
  price_minor: number | null
  price_currency: string | null
  price_basis: 'total' | 'per_person'

  ticket_required: number
  ticket_status: TicketStatus
  booking_url: string | null

  how_to_reach: string | null
  travel_mode: TravelMode | null
  travel_min: number | null
  travel_km: number | null

  address: string | null
  lat: number | null
  lng: number | null
  map_url: string | null
  opening_hours: string | null

  is_key: number
  booked: number
  status: ActivityStatus
  completed_at: number | null
  completed_by: string | null
  sort_order: number
}

export interface TravelDoc extends Synced {
  trip_id: string | null
  owner_user_id: string | null
  title: string
  kind: DocumentKind
  scope: 'global' | 'trip' | 'activity'
  r2_key: string
  mime: string | null
  size_bytes: number | null
  issued_on: string | null
  expires_on: string | null
  notes: string | null
}

export interface DocumentAttachment {
  document_id: string
  entity_type: 'item' | 'booking' | 'place'
  entity_id: string
  updated_at: number
}

export const DOCUMENT_KINDS: Array<{ id: DocumentKind; label: string; scope: 'global' | 'trip' }> = [
  { id: 'passport', label: 'Passport', scope: 'global' },
  { id: 'citizenship', label: 'Citizenship', scope: 'global' },
  { id: 'id', label: 'ID card', scope: 'global' },
  { id: 'licence', label: 'Driving licence', scope: 'global' },
  { id: 'insurance', label: 'Insurance', scope: 'global' },
  { id: 'visa', label: 'Visa', scope: 'trip' },
  { id: 'ticket', label: 'Ticket', scope: 'trip' },
  { id: 'hotel', label: 'Hotel voucher', scope: 'trip' },
  { id: 'permit', label: 'Permit', scope: 'trip' },
  { id: 'other', label: 'Other', scope: 'trip' },
]

export const TRAVEL_MODES: Record<TravelMode, string> = {
  walk: 'Walk', auto: 'Auto', car: 'Car', taxi: 'Taxi', train: 'Train',
  flight: 'Flight', boat: 'Boat', camel: 'Camel', other: 'Travel',
}

export interface Booking extends Synced {
  trip_id: string
  place_id: string | null
  kind: BookingKind
  ref: string | null
  vendor: string | null
  from_loc: string | null
  to_loc: string | null
  starts_at: number | null
  ends_at: number | null
  cost_minor: number | null
  currency: string
  status: 'booked' | 'waitlisted' | 'unbooked' | 'cancelled'
  doc_url: string | null
}

export interface Expense extends Synced {
  trip_id: string
  place_id: string | null
  amount_minor: number
  currency: string
  category: Category
  paid_by: string
  split_mode: SplitMode
  note: string | null
  spent_at: number
}

/** One category the party added. The built-in six have no row. */
export interface ExpenseCategory extends Synced {
  trip_id: string
  label: string
  icon: string | null
  sort_order: number
}

export interface ExpenseShare {
  expense_id: string
  user_id: string
  share_minor: number
  updated_at: number
}

export interface Note extends Synced {
  trip_id: string
  place_id: string | null
  body_md: string
  pinned: number
  guest_visible: number
}

export interface LinkRow extends Synced {
  trip_id: string
  place_id: string | null
  item_id: string | null
  url: string
  title: string | null
  kind: 'reel' | 'map' | 'doc' | 'article' | 'other'
}

export interface Outbox {
  opId: string
  table: string
  op: 'upsert' | 'delete'
  row: Record<string, unknown>
  shares?: Array<{ user_id: string; share_minor: number }>
  queuedAt: number
  attempts: number
}

export interface CategoryDef { id: Category; label: string; icon: string; builtin?: boolean }

/**
 * The six that always exist. They are constants rather than seeded rows so a
 * fresh database, or a phone that has not synced yet, still records a chai.
 * Custom ones live in `expense_categories` and are merged in by useCategories.
 */
export const BUILTIN_CATEGORIES: CategoryDef[] = [
  { id: 'food', label: 'Food', icon: '🍽', builtin: true },
  { id: 'transport', label: 'Transport', icon: '🚕', builtin: true },
  { id: 'stay', label: 'Stay', icon: '🛏', builtin: true },
  { id: 'tickets', label: 'Tickets', icon: '🎟', builtin: true },
  { id: 'shopping', label: 'Shopping', icon: '🛍', builtin: true },
  { id: 'other', label: 'Other', icon: '•', builtin: true },
]

/** A short set to pick from, so nobody has to find an emoji keyboard one-handed. */
export const CATEGORY_ICONS = [
  '🎁', '☕️', '💊', '📱', '🧺', '💈', '🛺', '⛽️', '🕌', '🐪',
  '💦', '🍬', '🧾', '🎭', '📷', '🧦', '🔌', '🪙', '🎈', '•',
]
